import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';

export const request = async (path, method = 'GET', data = null, signal = undefined) => {
  const response = await fetch(path, { method, signal, headers: { 'Content-Type': 'application/json' }, ...(data ? { body: JSON.stringify(data) } : {}) });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    // The status travels with the error. A caller that must tell "you may not see this" from "this
    // did not load" cannot do it by reading the message, and a page that guesses from the wording
    // breaks the day somebody rewords a refusal.
    const failure = new Error(payload?.message || `Request failed (${response.status}).`);
    failure.status = response.status;
    throw failure;
  }
  // An action that succeeded and had nothing to say answers 204 with no body, which is correct and
  // not an error. Treating an empty body as a broken service made every delete and every stop
  // report "the service returned an invalid response" after doing exactly what was asked.
  if (payload == null && (response.status === 204 || response.status === 205)) return {};
  if (payload == null) throw new Error('The service returned an invalid response.');
  return payload;
};
const Context = createContext(null);
const empty = { vehicles: [], sources: [], assignments: [], checkpoints: [], routes: [], drivers: [], config: {} };

// Each feed loads, times out and fails on its own, so a slow analytics or history query cannot hold
// back the vehicle, device and trip records that gate and yard work depend on.
const feeds = [
  ['data', '/api/operations/bootstrap', (data) => ({ data })],
  ['trips', '/api/operations/trips', (trips) => ({ trips })],
  ['analytics', '/api/operations/analytics', (analytics) => ({ analytics })],
  ['speed', '/api/operations/speed', (speed) => ({ speed })],
];
const noErrors = {
  data: null, trips: null, analytics: null, speed: null,
};

export const OperationsProvider = ({ children }) => {
  const [state, setState] = useState({
    data: empty, trips: [], speed: [], analytics: null, loading: true, errors: noErrors, updatedAt: null,
  });
  const active = useRef(null);
  const refresh = useCallback(async (background = false) => {
    active.current?.abort();
    const controller = new AbortController(); active.current = controller;
    const current = () => active.current === controller;
    if (!background) setState((s) => ({ ...s, loading: true }));
    const load = async ([key, path, apply]) => {
      const own = new AbortController(); const cancel = () => own.abort();
      controller.signal.addEventListener('abort', cancel);
      const timer = setTimeout(cancel, 15000);
      try {
        const value = await request(path, 'GET', null, own.signal);
        if (current()) setState((s) => ({ ...s, ...apply(value), errors: { ...s.errors, [key]: null } }));
      } catch (err) {
        if (current()) setState((s) => ({ ...s, errors: { ...s.errors, [key]: err.name === 'AbortError' ? 'did not respond in time.' : err.message } }));
      } finally {
        clearTimeout(timer); controller.signal.removeEventListener('abort', cancel);
      }
    };
    await Promise.all(feeds.map(load));
    if (current()) {
      setState((s) => ({
        ...s, loading: false, updatedAt: s.errors.data || s.errors.trips ? s.updatedAt : new Date().toISOString(),
      }));
    }
  }, []);
  useEffect(() => {
    refresh(); const timer = setInterval(() => { if (!document.hidden) refresh(true); }, 60000);
    return () => { active.current?.abort(); active.current = null; clearInterval(timer); };
  }, [refresh]);
  const mutate = useCallback(async (path, data, method = 'POST') => {
    const result = await request(`/api/operations/${path}`, method, data);
    await refresh(); return result;
  }, [refresh]);
  // `error` covers the records work depends on; a chart feed failing is reported separately.
  const value = useMemo(() => {
    const { errors } = state;
    const core = errors.data ? `Vehicles and devices ${errors.data.charAt(0).toLowerCase()}${errors.data.slice(1)}` : errors.trips && `Trips ${errors.trips.charAt(0).toLowerCase()}${errors.trips.slice(1)}`;
    const partial = [errors.analytics && 'performance figures', errors.speed && 'speed compliance'].filter(Boolean);
    return {
      ...state, error: core || null, partialError: partial.length ? `Could not refresh ${partial.join(' or ')}. Charts may be out of date.` : null, refresh, mutate,
    };
  }, [state, refresh, mutate]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
};
export const useOperations = () => useContext(Context);
